import {
  ArrowRight, BadgeCheck,
  LogOut,
  Menu,
  Minus,
  Moon,
  Package,
  Plus,
  ShoppingCart,
  Sun,
  User,
  X
} from "lucide-react";
import { createContext, useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import companyLogo from "@/assets/images/globo/logo-3d.png";
import RightSidebar from '@/components/Common/RightSidebar';

import Footer from './Footer';
import { AvatarIcon } from "@/components/Common/AvatarIcon";

// ─── Theme Context ─────────────────────────────────────────────────────────────
export const ThemeContext = createContext({ dark: false, toggleDark: () => { } });
export const useTheme = () => useContext(ThemeContext);

// ─── Mock data ─────────────────────────────────────────────────────────────────
const MOCK_CART = [
  {
    id: 1, name: "Aspirin 100mg", price: 12.99, qty: 2,
    category: "Pain Relief",
    iconClass: "text-danger", bgClass: "bg-danger-subtle",
    img: "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=80&h=80&fit=crop&auto=format",
  },
  {
    id: 2, name: "Metformin 500mg", price: 24.50, qty: 1,
    category: "Diabetes",
    iconClass: "text-primary", bgClass: "bg-primary-subtle",
    img: "https://images.unsplash.com/photo-1471864190281-a93a3070b6de?w=80&h=80&fit=crop&auto=format",
  },
  {
    id: 3, name: "Omeprazole 20mg", price: 15.25, qty: 1,
    category: "Gastro",
    iconClass: "text-warning", bgClass: "bg-warning-subtle",
    img: "https://images.unsplash.com/photo-1563213126-a4273aed2016?w=80&h=80&fit=crop&auto=format",
  },
];

// ─── Nav links — content/order/icons match the Globo Asiatico mockup ──────────
const NAV_LINKS = [
  { label: "My Profile", icon: User, link: "/globo-patient/dashboard" },
  { label: "My Orders", icon: Package, link: "/globo-patient/orders" },
  { label: "Order Medicine", icon: ShoppingCart, link: "/globo-patient/shop" },
];

// ─── Design tokens — Globo Asiatico palette (navy / teal / gold / blue) ───────
const T = {
  light: {
    pageBg: "#f3f3f9",
    navBg: "rgba(255,255,255,0.88)",
    navBorder: "#eaecf0",
    drawerBg: "#ffffff",
    itemBg: "#f8f9fa",
    itemBorder: "#e9ebec",
    footerBg: "#f3f3f9",
    footerBorder: "#e9ebec",
    labelColor: "#343a40",
    mutedColor: "#878a99",
    priceColor: "#405189",
    continueBtnBg: "transparent",
    continueBtnBorder: "#ced4da",
    continueBtnColor: "#6c757d",
    ink: "#101828",
    muted: "#667085",
    line: "#eaecf0",
    teal: "#0ab39c",
    tealSoft: "#e7f7f3",
    navLinkActiveColor: "#1e5f8a",
    navLinkActiveBg: "#e8f1f8",
    iconBtnBorder: "#eaecf0",
    iconBtnColor: "#667085",
    iconBtnHoverColor: "#101828",
    iconBtnHoverBorder: "#0ab39c",
    iconBtnHoverBg: "#e7f7f3",
    cartBadgeBg: "#e9b949",
    cartBadgeColor: "#3a2c00",
    avatarBg: "#e9b949",
    avatarColor: "#3a2c00",
    userchipBorder: "#eaecf0",
  },
  dark: {
    pageBg: "#1a1d2e",
    navBg: "rgba(15,34,56,0.88)",
    navBorder: "#1d2b3d",
    drawerBg: "#22263a",
    itemBg: "#2a2f47",
    itemBorder: "#2e3248",
    footerBg: "#1e2235",
    footerBorder: "#2e3248",
    labelColor: "#e9ebec",
    mutedColor: "#74788d",
    priceColor: "#8296d8",
    continueBtnBg: "transparent",
    continueBtnBorder: "#3a3f5a",
    continueBtnColor: "#74788d",
    ink: "#e8edf4",
    muted: "#93a2b5",
    line: "#1d2b3d",
    teal: "#0ab39c",
    tealSoft: "#0c3733",
    navLinkActiveColor: "#5fb0e0",
    navLinkActiveBg: "#10324f",
    iconBtnBorder: "#1d2b3d",
    iconBtnColor: "#93a2b5",
    iconBtnHoverColor: "#e8edf4",
    iconBtnHoverBorder: "#0ab39c",
    iconBtnHoverBg: "#0c3733",
    cartBadgeBg: "#e9b949",
    cartBadgeColor: "#3a2c00",
    avatarBg: "#e9b949",
    avatarColor: "#3a2c00",
    userchipBorder: "#1d2b3d",
  },
};

// ─── Reusable icon-button style (theme toggle / cart / logout / hamburger) ────
const iconBtnStyle = (tok) => ({
  width: "48px",
  height: "48px",
  borderRadius: "12px",
  border: "1px solid " + tok.iconBtnBorder,
  color: tok.iconBtnColor,
  background: "transparent",
  transition: "color .18s, border-color .18s, background-color .18s",
});
const iconBtnHover = (e, tok) => {
  e.currentTarget.style.color = tok.iconBtnHoverColor;
  e.currentTarget.style.borderColor = tok.iconBtnHoverBorder;
  e.currentTarget.style.backgroundColor = tok.iconBtnHoverBg;
};
const iconBtnLeave = (e, tok) => {
  e.currentTarget.style.color = tok.iconBtnColor;
  e.currentTarget.style.borderColor = tok.iconBtnBorder;
  e.currentTarget.style.backgroundColor = "transparent";
};

// ─── Component ────────────────────────────────────────────────────────────────
export default function AppLayout({ children }) {
  const [dark, setDark] = useState(false);
  const [cartItems, setCartItems] = useState(MOCK_CART);
  const [cartOpen, setCartOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const active = NAV_LINKS.find(n => location.pathname === n.link)?.label ?? "Dashboard";

  const tok = dark ? T.dark : T.light;

  const totalItems = cartItems.reduce((s, i) => s + i.qty, 0);
  const totalPrice = cartItems.reduce((s, i) => s + i.price * i.qty, 0);

  const updateQty = (id, d) =>
    setCartItems(prev => prev.map(i => i.id === id ? { ...i, qty: Math.max(1, i.qty + d) } : i));
  const removeItem = (id) =>
    setCartItems(prev => prev.filter(i => i.id !== id));

  const handleNavClick = (e, link) => {
    e.preventDefault();
    navigate(link);
  };

const raw = sessionStorage.getItem('currentUser'); // palitan ng tamang key
const obj = JSON.parse(raw);
console.log(obj,'Current User ');

  return (
    <ThemeContext.Provider value={{ dark, toggleDark: () => setDark(d => !d) }}>
      <div
        data-bs-theme={dark ? "dark" : "light"}
        style={{ minHeight: "100vh", backgroundColor: tok.pageBg, transition: "background-color 0.3s" }}
      >

        {/* ── TOPBAR ── */}
        <nav
          className="navbar border-bottom sticky-top px-3"
          style={{
            height: "72px",
            backgroundColor: tok.navBg,
            borderBottomColor: tok.navBorder,
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
            transition: "background-color 0.3s, border-color 0.3s",
          }}
        >
          <div
            className="d-flex align-items-center w-100 position-relative mx-auto"
            style={{ gap: "28px", maxWidth: "1240px" }}
          >
            {/* ── BRAND (left untouched) ── */}
            <a
              className="navbar-brand d-flex align-items-center gap-2 me-0 text-decoration-none"
              href="/globo-patient/dashboard"
              onClick={e => handleNavClick(e, "/globo-patient/dashboard")}
            >
              <img src={companyLogo} alt="" width="50px" height="50px" className="rounded-2" />
              <span className="fw-semibold fs-18 text-body">Globo Asiatico</span>
            </a>

            {/* ── NAV LINKS (desktop) ── */}
            <ul
              className="navbar-nav d-none d-lg-flex flex-row gap-1 position-absolute translate-middle-x mb-0"
              style={{ left: "45%" }}
            >
              {NAV_LINKS.map(({ label, icon: Icon, link }) => {
                const isActive = active === label;
                return (
                  <li className="nav-item" key={label}>
                    <a
                      href={link}
                      onClick={e => handleNavClick(e, link)}
                      className="d-flex align-items-center gap-2 text-decoration-none fw-medium"
                      style={{
                        fontSize: "15px",
                        padding: "9px 15px",
                        borderRadius: "11px",
                        color: isActive ? tok.navLinkActiveColor : tok.muted,
                        background: isActive ? tok.navLinkActiveBg : "transparent",
                        transition: "background-color .18s, color .18s",
                      }}
                      onMouseEnter={e => { if (!isActive) { e.currentTarget.style.backgroundColor = tok.navLinkActiveBg; e.currentTarget.style.color = tok.ink; } }}
                      onMouseLeave={e => { if (!isActive) { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = tok.muted; } }}
                    >
                      <Icon size={17} /> {label}
                    </a>
                  </li>
                );
              })}
            </ul>

            <div className="d-flex align-items-center gap-2 ms-auto">
              {/* Theme toggle */}
              <button
                className="btn d-flex align-items-center justify-content-center"
                style={iconBtnStyle(tok)}
                onMouseEnter={e => iconBtnHover(e, tok)}
                onMouseLeave={e => iconBtnLeave(e, tok)}
                onClick={() => setDark(d => !d)}
                title="Toggle theme"
              >
                {dark ? <Moon size={18} /> : <Sun size={18} />}
              </button>

              {/* Cart / Order Medicine */}
              <button
                className="btn d-flex align-items-center justify-content-center position-relative"
                style={iconBtnStyle(tok)}
                onMouseEnter={e => iconBtnHover(e, tok)}
                onMouseLeave={e => iconBtnLeave(e, tok)}
                onClick={() => setCartOpen(true)}
                title="Order Medicine"
              >
                <ShoppingCart size={18} />
                {totalItems > 0 && (
                  <span
                    className="position-absolute fw-bold d-flex align-items-center justify-content-center"
                    style={{
                      top: "-6px", right: "-6px",
                      background: tok.cartBadgeBg, color: tok.cartBadgeColor,
                      fontSize: "11px", minWidth: "19px", height: "19px",
                      borderRadius: "10px", padding: "0 5px",
                    }}
                  >
                    {totalItems}
                  </span>
                )}
              </button>

              {/* ── USER CHIP (no dropdown — click goes straight to profile) ── */}
              <button
                className="btn d-none d-lg-flex align-items-center gap-2"
                style={{
                  padding: "5px 12px 5px 5px",
                  borderRadius: "30px",
                  border: "1px solid " + tok.userchipBorder,
                  background: "transparent",
                  transition: "border-color .18s",
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = tok.iconBtnHoverBorder; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = tok.userchipBorder; }}
                onClick={e => handleNavClick(e, "/globo-patient/dashboard")}
              >
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center fw-bold flex-shrink-0"
                  style={{ width: "34px", height: "34px", background: tok.avatarBg, color: tok.avatarColor, fontSize: "13px" }}
                >
                  <AvatarIcon  avatarImg={obj?.avatar} avatarStyle={{ width: "34px", height: "34px",  marginLeft: '16px' }} />
                </div>
                <div className="d-none d-xl-block text-start lh-1">
                  <div className="fw-semibold" style={{ fontSize: "13.5px", color: tok.ink }}>{obj?.firstName}</div>
                  <div style={{ fontSize: "11px", color: tok.muted }}>{obj?.patientCode}</div>
                </div>
              </button>

              {/* Logout */}
              <button
                className="btn d-none d-lg-flex align-items-center justify-content-center"
                style={iconBtnStyle(tok)}
                onMouseEnter={e => iconBtnHover(e, tok)}
                onMouseLeave={e => iconBtnLeave(e, tok)}
                onClick={e => handleNavClick(e, "/login")}
                title="Log out"
              >
                <LogOut size={18} />
              </button>

              {/* Hamburger (mobile) */}
              <button
                className="btn d-lg-none d-flex align-items-center justify-content-center"
                style={iconBtnStyle(tok)}
                onMouseEnter={e => iconBtnHover(e, tok)}
                onMouseLeave={e => iconBtnLeave(e, tok)}
                onClick={() => setMenuOpen(o => !o)}
              >
                {menuOpen ? <X size={18} /> : <Menu size={18} />}
              </button>
            </div>
          </div>
        </nav>

        {/* ── MOBILE MENU ── */}
        {menuOpen && (
          <div
            className="d-lg-none px-3 pb-3"
            style={{ backgroundColor: tok.navBg, borderBottom: "1px solid " + tok.navBorder, transition: "background-color 0.3s" }}
          >
            {NAV_LINKS.map(({ label, icon: Icon, link }) => {
              const isActive = active === label;
              return (
                <a
                  key={label}
                  href={link}
                  className="d-flex align-items-center gap-2 px-3 py-2 rounded-2 mb-1 text-decoration-none fw-medium"
                  style={{
                    fontSize: "13.5px",
                    color: isActive ? tok.navLinkActiveColor : tok.muted,
                    background: isActive ? tok.navLinkActiveBg : "transparent",
                  }}
                  onClick={e => { handleNavClick(e, link); setMenuOpen(false); }}
                >
                  <Icon size={15} /> {label}
                </a>
              );
            })}

            <hr className="my-2" style={{ borderColor: tok.navBorder, opacity: 1 }} />

            <div
              className="d-flex align-items-center gap-2 px-3 py-2 rounded-2"
              style={{ fontSize: "13.5px", color: tok.muted, cursor: "pointer" }}
              onClick={e => { handleNavClick(e, "/login"); setMenuOpen(false); }}
            >
              <LogOut size={15} /> Log out
            </div>
          </div>
        )}

        <main>{children}</main>

        {/* ── BACKDROP ── */}
        {cartOpen && (
          <div className="offcanvas-backdrop fade show" onClick={() => setCartOpen(false)} />
        )}

        {/* ── CART DRAWER (unchanged for now) ── */}
        <div
          className={"offcanvas offcanvas-end" + (cartOpen ? " show" : "")}
          style={{
            width: "min(100vw, 420px)",
            visibility: cartOpen ? "visible" : "hidden",
            backgroundColor: tok.drawerBg,
            display: "flex",
            flexDirection: "column",
            transition: "background-color 0.3s",
          }}
        >

          {/* Header */}
          <div
            className="d-flex align-items-center justify-content-between px-4 py-3 flex-shrink-0"
            style={{ background: "linear-gradient(135deg, #405189 0%, #364474 100%)" }}
          >
            <div className="d-flex align-items-center gap-2">
              <ShoppingCart size={18} color="rgba(255,255,255,0.85)" />
              <span className="fw-semibold text-white" style={{ fontSize: "16px", letterSpacing: "-0.2px" }}>
                Your Cart
              </span>
              <span
                className="badge rounded-pill fw-bold text-dark"
                style={{ background: "#c9a84c", fontSize: "10px", padding: "3px 7px" }}
              >
                {totalItems}
              </span>
            </div>
            <button
              className="btn d-flex align-items-center justify-content-center rounded-2"
              style={{
                width: "32px", height: "32px",
                background: "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.2)",
                color: "#fff",
              }}
              onClick={() => setCartOpen(false)}
            >
              X
            </button>
          </div>

          {/* Item list */}
          <div
            className="d-flex flex-column gap-2 p-3"
            style={{ flex: 1, overflowY: "auto" }}
          >
            {cartItems.length === 0 ? (
              <div
                className="d-flex flex-column align-items-center justify-content-center text-center"
                style={{ flex: 1, padding: "48px 20px" }}
              >
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center mb-3"
                  style={{
                    width: "72px", height: "72px",
                    background: tok.itemBg,
                    border: "1.5px solid " + tok.itemBorder,
                  }}
                >
                  <ShoppingCart size={30} color={tok.mutedColor} />
                </div>
                <p className="fw-semibold mb-1" style={{ color: tok.labelColor, fontSize: "15px" }}>
                  Your cart is empty
                </p>
                <p style={{ color: tok.mutedColor, fontSize: "13px", margin: 0 }}>
                  Add medications from the catalog.
                </p>
              </div>
            ) : (
              cartItems.map(item => (
                <div
                  key={item.id}
                  className="d-flex overflow-hidden rounded-3"
                  style={{
                    background: tok.itemBg,
                    border: "1px solid " + tok.itemBorder,
                    minHeight: "88px",
                  }}
                >
                  {/* Product image */}
                  <div
                    className={"flex-shrink-0 d-flex align-items-center justify-content-center " + item.bgClass}
                    style={{ width: "88px", minHeight: "88px", position: "relative", overflow: "hidden" }}
                  >
                    <img
                      src={item.img}
                      alt={item.name}
                      style={{ width: "100%", height: "100%", objectFit: "cover", position: "absolute", inset: 0 }}
                      onError={e => { e.currentTarget.style.display = "none"; }}
                    />
                  </div>

                  {/* Content */}
                  <div
                    className="d-flex flex-column justify-content-between py-2 px-3 flex-grow-1 min-w-0"
                    style={{ gap: "6px" }}
                  >
                    {/* Top row: name + remove */}
                    <div className="d-flex align-items-start justify-content-between gap-2">
                      <div className="min-w-0">
                        <div
                          className="fw-semibold lh-sm"
                          style={{ color: tok.labelColor, fontSize: "13.5px" }}
                        >
                          {item.name}
                        </div>
                        <div style={{ color: tok.mutedColor, fontSize: "11px", marginTop: "2px" }}>
                          {item.category}
                        </div>
                      </div>
                      <button
                        className="btn p-0 d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{
                          width: "22px", height: "22px",
                          background: "transparent",
                          border: "none",
                          color: tok.mutedColor,
                          marginTop: "1px",
                        }}
                        onClick={() => removeItem(item.id)}
                        title="Remove"
                      >
                        <X size={13} />
                      </button>
                    </div>

                    {/* Bottom row: price + stepper */}
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <span className="fw-bold" style={{ color: tok.priceColor, fontSize: "15px" }}>
                          ${(item.price * item.qty).toFixed(2)}
                        </span>
                        <span style={{ color: tok.mutedColor, fontSize: "10.5px", marginLeft: "5px" }}>
                          ${item.price.toFixed(2)} ea.
                        </span>
                      </div>

                      {/* Qty stepper */}
                      <div
                        className="d-flex align-items-center overflow-hidden rounded-2"
                        style={{ border: "1px solid " + tok.itemBorder, height: "28px" }}
                      >
                        <button
                          className="btn p-0 d-flex align-items-center justify-content-center border-0"
                          style={{
                            width: "28px", height: "28px",
                            background: tok.itemBorder,
                            color: tok.mutedColor,
                            borderRadius: 0,
                            flexShrink: 0,
                          }}
                          onClick={() => updateQty(item.id, -1)}
                        >
                          <Minus size={10} />
                        </button>
                        <span
                          className="fw-semibold d-flex align-items-center justify-content-center"
                          style={{
                            width: "30px", height: "28px",
                            fontSize: "13px",
                            color: tok.labelColor,
                            background: tok.itemBg,
                          }}
                        >
                          {item.qty}
                        </span>
                        <button
                          className="btn p-0 d-flex align-items-center justify-content-center border-0"
                          style={{
                            width: "28px", height: "28px",
                            background: "#405189",
                            color: "#fff",
                            borderRadius: 0,
                            flexShrink: 0,
                          }}
                          onClick={() => updateQty(item.id, 1)}
                        >
                          <Plus size={10} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {cartItems.length > 0 && (
            <div
              className="p-3 flex-shrink-0"
              style={{
                background: tok.footerBg,
                borderTop: "1px solid " + tok.footerBorder,
                transition: "background-color 0.3s",
              }}
            >
              <div className="d-flex justify-content-between mb-1">
                <span style={{ color: tok.mutedColor, fontSize: "13px" }}>Items ({cartItems.length})</span>
                <span className="fw-medium" style={{ color: tok.labelColor, fontSize: "13px" }}>
                  ${totalPrice.toFixed(2)}
                </span>
              </div>
              <div className="d-flex justify-content-between mb-3">
                <span style={{ color: tok.mutedColor, fontSize: "13px" }}>Shipping</span>
                <span className="fw-semibold text-success" style={{ fontSize: "13px" }}>Free</span>
              </div>

              {/* Total row */}
              <div
                className="d-flex justify-content-between align-items-center py-2 mb-3"
                style={{ borderTop: "1px solid " + tok.footerBorder }}
              >
                <span className="fw-semibold" style={{ color: tok.labelColor, fontSize: "14px" }}>Total</span>
                <span className="fw-bold" style={{ color: "#405189", fontSize: "22px", lineHeight: 1 }}>
                  ${totalPrice.toFixed(2)}
                </span>
              </div>

              {/* Checkout */}
              <button
                className="btn w-100 d-flex align-items-center justify-content-center gap-2 fw-semibold mb-2"
                style={{
                  height: "46px",
                  background: "linear-gradient(135deg, #405189 0%, #364474 100%)",
                  border: "none",
                  borderRadius: "10px",
                  color: "#fff",
                  fontSize: "14px",
                  boxShadow: "0 4px 14px rgba(64,81,137,0.35)",
                }}
              >
                <BadgeCheck size={16} />
                Proceed to Checkout
                <ArrowRight size={14} />
              </button>

              {/* Continue shopping */}
              <button
                className="btn w-100 fw-medium"
                style={{
                  height: "40px",
                  background: tok.continueBtnBg,
                  border: "1.5px solid " + tok.continueBtnBorder,
                  borderRadius: "10px",
                  color: tok.continueBtnColor,
                  fontSize: "13px",
                  transition: "all 0.15s",
                }}
                onClick={() => setCartOpen(false)}
              >
                Continue Shopping
              </button>
            </div>
          )}
        </div>

        <Footer />

      </div>
      <RightSidebar />
    </ThemeContext.Provider>
  );
}